import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import type { MutationFetcher } from 'swr/mutation';
import useSWRMutation from 'swr/mutation';

import Feedback from '~/components/feedback/Feedback';
import { chatCreate } from '~/data/generated/endpoints/chat/chat';
import type { ChatAnswer, ChatQuestionRequest } from '~/data/generated/model';
import { CARD_SX, pageBandColor, SURFACE_RADIUS } from '~/theme';

/**
 * TODO: Eventually we'll make this generic for all entities — the endpoint takes
 * a `collection`, and only 'contract' is wired up today.
 */
const chatFetcher: MutationFetcher<ChatAnswer, string, ChatQuestionRequest> = async (
  _key,
  { arg: { query, entityId, metadata } },
) => {
  const { data } = await chatCreate({ collection: 'contract', query, entityId, metadata });
  // `chatCreateResponse` is a status-discriminated union, but `customFetch`
  // (liftApiMutator.ts) already throws on any non-2xx before this line, so `data` is
  // always the `ChatAnswer` branch.
  return data as ChatAnswer;
};

/** Openers, so the empty box isn't a blank prompt. */
const ASK_PLACEHOLDER =
  'Is vandalism covered under this contract? Are callback charges billable under this agreement?...';

/**
 * How tall the answers get before they scroll inside the panel. Left to grow, every
 * question asked would push the fields and tabs below further down the page.
 */
const ANSWERS_MAX_HEIGHT = 220;

/** One exchange, kept so a second question doesn't erase the first. */
type Exchange = { question: string; answer: string; runId: string | null };

/**
 * Ask a question of the contract document — a panel beside the contract's title, in
 * the space the invoice page gives its AI review card. What the model has to say about
 * the record reads with the record, rather than behind a button as it did before.
 */
export function ContractAskPanel({
  contractId,
  canChat,
}: {
  contractId: string;
  /** False when the PDF has no text layer — there is nothing to read answers out of. */
  canChat: boolean;
}) {
  const [query, setQuery] = useState('');
  const [history, setHistory] = useState<Exchange[]>([]);
  const { trigger, isMutating, error } = useSWRMutation('chat', chatFetcher);

  const ask = async (question: string) => {
    const trimmed = question.trim();
    if (!trimmed || isMutating || !canChat) return;

    setQuery('');
    const answer = await trigger({ collection: 'contract', query: trimmed, entityId: contractId });
    if (answer) {
      // Newest first: the answer to what was just asked shouldn't sit below a
      // screen of earlier ones.
      setHistory((previous) => [
        { question: trimmed, answer: answer.answer, runId: answer.runId },
        ...previous,
      ]);
    }
  };

  return (
    <Box
      sx={{
        p: 1.5,
        // The band's own tint, not a white card on top of it: the panel is part of the header
        // rather than a surface floating in it. No border either — on a field of the same
        // colour it drew a box around nothing.
        borderRadius: `${SURFACE_RADIUS}px`,
        bgcolor: pageBandColor,
      }}
    >
      <Stack gap={1.25}>
        <Stack direction="row" alignItems="center" gap={1}>
          <AutoAwesomeIcon fontSize="small" color="primary" />
          <Typography variant="caption" color="text.secondary" letterSpacing={0.5}>
            ASK THIS CONTRACT (PDF AI ANALYSIS)
          </Typography>
        </Stack>

        {!canChat ? (
          <Alert severity="info">
            The uploaded PDF has no readable text — it was scanned as images, so there is nothing to
            read answers out of. Your account manager can have it re-processed.
          </Alert>
        ) : (
          <>
            <TextField
              fullWidth
              multiline
              maxRows={3}
              size="small"
              placeholder={ASK_PLACEHOLDER}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                // Enter sends; Shift+Enter is a newline, since a question can run to a second line.
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  void ask(query);
                }
              }}
              slotProps={{
                input: {
                  sx: { borderRadius: 3, alignItems: 'flex-end', bgcolor: 'background.paper' },
                  endAdornment: isMutating ? (
                    <CircularProgress size={20} sx={{ m: 1 }} />
                  ) : (
                    <IconButton
                      color="primary"
                      aria-label="Ask"
                      disabled={query.trim().length === 0}
                      onClick={() => void ask(query)}
                    >
                      <ArrowUpwardIcon />
                    </IconButton>
                  ),
                },
              }}
            />

            {error ? (
              <Alert severity="error">
                That question could not be answered just now. Please try again.
              </Alert>
            ) : null}

            {history.length > 0 ? (
              <Stack
                gap={2}
                sx={{ maxHeight: ANSWERS_MAX_HEIGHT, overflowY: 'auto', pr: 0.5, mt: 0.25 }}
              >
                {history.map((exchange, index) => (
                  <Stack key={`${exchange.runId ?? 'answer'}-${index}`} gap={0.75}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {exchange.question}
                    </Typography>
                    <Box sx={{ ...CARD_SX, p: 1.5, bgcolor: 'background.paper' }}>
                      <Typography variant="body2" whiteSpace="pre-wrap">
                        {exchange.answer}
                      </Typography>
                    </Box>
                    <Feedback feedbackKey="chat" runId={exchange.runId} />
                  </Stack>
                ))}
              </Stack>
            ) : null}
          </>
        )}
      </Stack>
    </Box>
  );
}
